import type PublishingSettings from 'owa-service/lib/contract/PublishingSettings';
import type {
    BookingBusinessEntry,
    BookingSettings,
    BookingSchedulingPolicy,
    BookingThemeData,
    BookingSelfServicePageAuthType,
    BookingOtpSettings,
    BookingEmailVerificationMethod,
    BookingOnDemandServicesSettings,
} from 'owa-graph-schema';
import mapBookingSchedulingPolicy from './mapBookingSchedulingPolicy';
import mapBookingThemeData from './mapBookingThemeData';
import mapBookingSelfServicePageAuthType from './mapBookingSelfServicePageAuthType';
import mapBookingOtpSettings from './mapBookingOtpSettings';
import mapBookingEmailVerificationMethod from './modelB2/mapBookingEmailVerificationMethod';
import mapBookingOnDemandServicesSettings from './modelB2/mapBookingOnDemandServicesSettings';

const mapBookingBusinessEntryToPublishingSettings = (
    businessInfo: BookingBusinessEntry,
    source?: string
): PublishingSettings => {
    const { isPublished, schedulingPolicy, settings } = businessInfo;
    const {
        otpVerificationSetting,
        publishingUrl,
        themeData,
        c2RevampedEnabled,
        authType,
        friendlyUrl,
        isLogoHidden,
        isConsentNeeded,
        consentText,
        customColorScheme,
        isSearchEngineIndexabilityDisabled,
        showTimeSlotsInBusinessTimeZone,
        emailVerificationMethod,
        sendMeetingInviteToCustomer,
        onDemandServicesSetting,
    } = settings as BookingSettings;

    const mappedSchedulingPolicy = mapBookingSchedulingPolicy(
        schedulingPolicy as BookingSchedulingPolicy,
        source
    );
    /** Since, in B2 sendMeetingInviteToCustomer is part of settings instead of schedulingPolicy
     * so, mapping it here.
     */
    if (!!mappedSchedulingPolicy) {
        mappedSchedulingPolicy.SendMeetingInviteToCustomer = !!sendMeetingInviteToCustomer;
    }

    const publishingSettings = {
        IsPublished: isPublished ?? true,
        PublishedUrl: publishingUrl ?? '',
        FriendlyUrl: friendlyUrl ?? '',
        DefaultSchedulingPolicy: mappedSchedulingPolicy,
        Theme: mapBookingThemeData(themeData as BookingThemeData),
        IsLogoHidden: isLogoHidden ?? false,
        IsConsentNeeded: isConsentNeeded ?? false,
        ConsentText: consentText ?? '',
        AuthType: mapBookingSelfServicePageAuthType(authType as BookingSelfServicePageAuthType),
        CustomColorScheme: customColorScheme ?? '',
        IsSearchEngineIndexabilityDisabled: isSearchEngineIndexabilityDisabled ?? false,
        OtpVerificationSetting: mapBookingOtpSettings(otpVerificationSetting as BookingOtpSettings),
        IsNewC2RevampedEnabled: c2RevampedEnabled ?? true,
        ShowTimeSlotsInBusinessTimeZone: showTimeSlotsInBusinessTimeZone ?? false,
        EmailVerificationMethod: mapBookingEmailVerificationMethod(
            emailVerificationMethod as BookingEmailVerificationMethod
        ),
        OnDemandServicesSetting: mapBookingOnDemandServicesSettings(
            onDemandServicesSetting as BookingOnDemandServicesSettings
        ),
    };

    return publishingSettings;
};

export default mapBookingBusinessEntryToPublishingSettings;
